import { useMemo, useRef, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-premium';
import { CircleCheckIcon, Trash2Icon } from 'lucide-react';

import type {
  GetListingMlsSettingsQuery,
  GetMlsNetworkTeamsQuery,
  Mls_Commission_Type_Enum_Enum,
  Mls_Network_Teams_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import {
  Filters,
  type QuickFilterDefinition,
} from '@modules/_shared/components/filters/Filters';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { canUpdateMls } from '@modules/_shared/utils/listingPermissions';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import { MarketingHomogeneousTitle } from '@modules/listings/ListingMarketing';
import {
  type MlsMutationOutcome,
  runMlsMutation,
  useMlsTracking,
} from '@modules/mls/mlsAnalytics';
import {
  GET_LISTING_MLS_SETTINGS,
  GET_MLS_NETWORK_TEAMS,
  GET_MLS_SHARED_TEAM_DETAILS,
  HANDLE_MLS_SHARE_REQUEST,
  REMOVE_LISTING_SHARE,
  SHARE_LISTING_WITH_TEAM,
  UPDATE_LOT_MLS_FIELD,
} from '@modules/mls/mlsQueries';
import { MLS_TOUR_ANCHORS } from '@modules/mls/mlsTours';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { ANALYTICS_EVENTS } from '@shared/analytics';
import { useLocale } from '@shared/hooks/locale';

type ShareRequest = NonNullable<
  GetListingMlsSettingsQuery['share_requests']
>[number];
type ListingShare = NonNullable<
  GetListingMlsSettingsQuery['listing_shares']
>[number];
type NetworkTeam = NonNullable<
  GetMlsNetworkTeamsQuery['network_teams']
>[number];

interface ListingMlsSettingsProps {
  lotId: string;
  readOnly?: boolean;
}

export const ListingMlsSettings: React.FC<ListingMlsSettingsProps> = ({
  lotId,
  readOnly = false,
}) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const { trackMlsMutation } = useMlsTracking(me);
  const countryCode = me?.tenant?.country_code ?? 'CH';
  const currentUserId = me?.id ?? '';

  // URL params for server-side pagination and filtering
  const [searchParams] = useScopedSearchParams('mls_network');
  const { limit, offset } = getPaginationVariables(searchParams, 10);

  // Parse where clause from URL params (set by QuickFilters).
  // `prepareWhereClauseQuery` rewrites `places` keys into a flattened list of
  // state_id / locality_id / ... checks on the parent table. The
  // `mls_network_teams` view denormalises those id columns directly (so we
  // can filter cross-tenant teams by location without going through the
  // RLS-restricted `teams` relationship), which makes the rewrite the right
  // shape here.
  const parsedWhere: Mls_Network_Teams_Bool_Exp = useMemo(() => {
    const whereParam = searchParams.get('where');
    if (!whereParam) {
      return {};
    }
    try {
      return prepareWhereClauseQuery(JSON.parse(whereParam));
    } catch {
      return {};
    }
  }, [searchParams]);

  // Build final where clause by merging country filter with parsed filters
  const networkTeamsWhere: Mls_Network_Teams_Bool_Exp = useMemo(() => {
    return {
      country_code: { _eq: countryCode },
      ...parsedWhere,
    };
  }, [countryCode, parsedWhere]);

  // Check if any filters are active (for empty state messaging)
  const hasActiveFilters = Object.keys(parsedWhere).length > 0;

  // Separate queries for better caching - settings change often, network teams don't
  const { data, loading, error, refetch } = useQuery(GET_LISTING_MLS_SETTINGS, {
    variables: { lotId },
  });

  const isSharedOnMls = data?.lot?.shared_on_mls_at != null;

  const { data: networkData, loading: networkLoading } = useQuery(
    GET_MLS_NETWORK_TEAMS,
    {
      variables: {
        where: networkTeamsWhere,
        countryCode,
        limit,
        offset,
      },
      skip: !isSharedOnMls,
    },
  );

  const sharedTeamIdList = useMemo(
    () => (data?.listing_shares ?? []).map((s: ListingShare) => s.team_id),
    [data?.listing_shares],
  );

  const { data: sharedTeamsData } = useQuery(GET_MLS_SHARED_TEAM_DETAILS, {
    variables: { teamIds: sharedTeamIdList },
    skip: sharedTeamIdList.length === 0,
  });

  const sharedTeamsById = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof sharedTeamsData>['shared_teams'][number]
    >();
    sharedTeamsData?.shared_teams?.forEach(t => {
      if (t.id) {
        map.set(t.id, t);
      }
    });
    return map;
  }, [sharedTeamsData]);

  const handleMutationError = (error: Error) => {
    console.error('MLS mutation error:', error);
    setErrorMessage(t('An error occurred. Please try again.'));
  };

  const [updateMlsField, { loading: updating }] = useMutation(
    UPDATE_LOT_MLS_FIELD,
    {
      onError: handleMutationError,
      refetchQueries: [
        { query: GET_LISTING_MLS_SETTINGS, variables: { lotId } },
      ],
    },
  );
  const [shareWithTeam, { loading: sharing }] = useMutation(
    SHARE_LISTING_WITH_TEAM,
    { onError: handleMutationError },
  );
  const [removeShare, { loading: removing }] = useMutation(
    REMOVE_LISTING_SHARE,
    { onError: handleMutationError },
  );
  const [handleRequest, { loading: handlingRequest }] = useMutation(
    HANDLE_MLS_SHARE_REQUEST,
    { onError: handleMutationError },
  );

  const lot = data?.lot;
  const networkTeams = useMemo(
    () => networkData?.network_teams ?? [],
    [networkData?.network_teams],
  );
  // Filtered count (for pagination)
  const networkTeamsCount =
    networkData?.network_teams_aggregate?.aggregate?.count ?? 0;
  // Total count (for Members stat - not affected by search/place filters)
  const networkTeamsTotal =
    networkData?.network_teams_total?.aggregate?.count ?? 0;
  const listingShares = data?.listing_shares ?? [];
  const shareRequests = data?.share_requests ?? [];

  const pendingRequests = shareRequests.filter(
    (r: ShareRequest) => r.status === 'pending',
  );
  const sharedTeamIds = new Set(
    listingShares.map((s: ListingShare) => s.team_id),
  );

  // Get team IDs that have published this listing on their website
  const publishedTeamIds = useMemo(() => {
    const teamIds = new Set<string>();
    lot?.portal_listings?.forEach(pl => {
      if (pl.portal?.team_id) {
        teamIds.add(pl.portal.team_id);
      }
    });
    return teamIds;
  }, [lot?.portal_listings]);

  // Commission state
  const [commissionPercent, setCommissionPercent] = useState<string>(
    lot?.mls_buyer_agent_share_percent?.toString() ?? '',
  );
  const [commissionFixed, setCommissionFixed] = useState<string>(
    lot?.mls_buyer_agent_share_fixed_amount?.toString() ?? '',
  );
  const [mlsNote, setMlsNote] = useState<string>(lot?.mls_note ?? '');

  // Re-sync local input state only when the user navigates to a different
  // lot. Depending on the individual field values would let a parent refetch
  // that returns the same lotId overwrite the user's in-progress input
  // mid-typing, which among other things masks the onBlur diff-check and
  // silently drops the save.
  const lastSyncedLotIdRef = useRef<string | null | undefined>(lotId);
  if (lastSyncedLotIdRef.current !== lotId) {
    lastSyncedLotIdRef.current = lotId;
    setCommissionPercent(lot?.mls_buyer_agent_share_percent?.toString() ?? '');
    setCommissionFixed(
      lot?.mls_buyer_agent_share_fixed_amount?.toString() ?? '',
    );
    setMlsNote(lot?.mls_note ?? '');
  }

  // Share mode change confirmation dialog
  const [shareModeDialogOpen, setShareModeDialogOpen] = useState(false);
  const [pendingShareMode, setPendingShareMode] = useState<string | null>(null);

  // Remove-access confirmation dialog
  const [pendingRemoveShare, setPendingRemoveShare] = useState<{
    shareId: string;
    teamName?: string;
    teamId?: string;
  } | null>(null);

  // Error snackbar
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // QuickFilters for the network teams table.
  // `places` is a computed field on mls_network_teams that returns the team's
  // HQ places — going through it (instead of `team.places`) keeps the filter
  // working for cross-tenant teams. `prepareWhereClauseQuery` rewrites the
  // resulting `places.id._in` into checks on the view's denormalised place_id
  // columns.
  const networkTeamsQuickFilters: QuickFilterDefinition[] = useMemo(
    () => [
      {
        label: t('Location'),
        path: ['mls_network_teams_bool_exp', 'places'],
      },
      {
        label: t('Agency'),
        // Filter by id directly on mls_network_teams (cross-tenant safe view).
        // Filtering via `team` would go through the RLS-filtered teams table.
        path: ['mls_network_teams_bool_exp', 'id'],
      },
    ],
    [t],
  );

  // Get counts for table columns
  const getTeamRequestedCount = (teamId: string) => {
    return pendingRequests.filter((r: ShareRequest) => r.team_id === teamId)
      .length;
  };

  const isTeamPublished = (teamId: string) => {
    return publishedTeamIds.has(teamId);
  };

  // Explains how to share, for whoever may change this listing's MLS setup.
  useAutoTour(
    'mls-share-listing',
    lot != null && readOnly === false && canUpdateMls(me, lot),
  );

  if (loading) {
    return (
      <LinearProgress
        sx={{
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 2000,
        }}
      />
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  if (!lot) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('No listing data found')}
      </Alert>
    );
  }

  const canUpdate = readOnly === false && canUpdateMls(me, lot);

  // Terms edits (toggle / commission type / rate / note) are one config-edit
  // signal, PostHog-only, tracked with the mutation's outcome.
  const trackTermsUpdated = (
    field: string,
    outcome: MlsMutationOutcome,
    // `lot` still holds the pre-mutation cached value here (updateMlsField
    // refetches without awaitRefetchQueries), so a handler that changes one of
    // these fields must pass the new value explicitly.
    overrides?: { hasNote?: boolean; commissionType?: string },
  ) => {
    trackMlsMutation(
      ANALYTICS_EVENTS.MLS_TERMS_UPDATED,
      ANALYTICS_EVENTS.MLS_TERMS_UPDATE_FAILED,
      {
        lot_id: lotId,
        field,
        commission_type:
          overrides?.commissionType ?? lot.mls_commission_type ?? 'percentage',
        has_note: overrides?.hasNote ?? (lot.mls_note ?? '') !== '',
      },
      outcome,
    );
  };

  const handleToggleMls = async () => {
    const turningOn = lot.shared_on_mls_at == null;
    const newValue = turningOn ? new Date().toISOString() : null;
    const outcome = await runMlsMutation(() =>
      updateMlsField({
        variables: { lotId, changes: { shared_on_mls_at: newValue } },
      }),
    );
    // Lifecycle (→ PostHog + Customer.io on success only): supply-side signal.
    trackMlsMutation(
      turningOn
        ? ANALYTICS_EVENTS.MLS_LISTING_SHARED
        : ANALYTICS_EVENTS.MLS_LISTING_UNSHARED,
      turningOn
        ? ANALYTICS_EVENTS.MLS_LISTING_SHARE_FAILED
        : ANALYTICS_EVENTS.MLS_LISTING_UNSHARE_FAILED,
      turningOn
        ? { lot_id: lotId, share_mode: lot.mls_share_mode ?? 'individual' }
        : { lot_id: lotId },
      outcome,
      { lifecycle: true },
    );
  };

  const handleShareModeChange = (mode: string) => {
    // Warn when switching from individual to everyone if there are individual shares
    if (
      lot.mls_share_mode === 'individual' &&
      mode === 'everyone' &&
      listingShares.length > 0
    ) {
      setPendingShareMode(mode);
      setShareModeDialogOpen(true);
      return;
    }
    applyShareModeChange(mode);
  };

  const applyShareModeChange = async (mode: string) => {
    const from = lot.mls_share_mode ?? 'individual';
    const outcome = await runMlsMutation(() =>
      updateMlsField({
        variables: { lotId, changes: { mls_share_mode: mode } },
      }),
    );
    trackMlsMutation(
      ANALYTICS_EVENTS.MLS_SHARE_MODE_CHANGED,
      ANALYTICS_EVENTS.MLS_SHARE_MODE_CHANGE_FAILED,
      { lot_id: lotId, from, to: mode },
      outcome,
    );
  };

  const handleShareModeDialogConfirm = () => {
    if (pendingShareMode) {
      applyShareModeChange(pendingShareMode);
    }
    setShareModeDialogOpen(false);
    setPendingShareMode(null);
  };

  const handleShareModeDialogCancel = () => {
    setShareModeDialogOpen(false);
    setPendingShareMode(null);
  };

  const handleTermsToggle = async () => {
    const outcome = await runMlsMutation(() =>
      updateMlsField({
        variables: {
          lotId,
          changes: { mls_terms_enabled: !lot.mls_terms_enabled },
        },
      }),
    );
    trackTermsUpdated('terms_enabled', outcome);
  };

  const handleCommissionTypeChange = async (
    type: Mls_Commission_Type_Enum_Enum,
  ) => {
    const outcome = await runMlsMutation(() =>
      updateMlsField({
        variables: { lotId, changes: { mls_commission_type: type } },
      }),
    );
    trackTermsUpdated('commission_type', outcome, { commissionType: type });
  };

  const handleCommissionPercentBlur = async () => {
    const value = parseFloat(commissionPercent);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      const outcome = await runMlsMutation(() =>
        updateMlsField({
          variables: {
            lotId,
            changes: { mls_buyer_agent_share_percent: value },
          },
        }),
      );
      trackTermsUpdated('rate', outcome);
    }
  };

  const handleCommissionFixedBlur = async () => {
    const value = parseFloat(commissionFixed);
    if (!isNaN(value) && value >= 0) {
      const outcome = await runMlsMutation(() =>
        updateMlsField({
          variables: {
            lotId,
            changes: { mls_buyer_agent_share_fixed_amount: value },
          },
        }),
      );
      trackTermsUpdated('rate', outcome);
    }
  };

  const handleShareWithTeam = async (teamId: string) => {
    const outcome = await runMlsMutation(() =>
      shareWithTeam({
        variables: {
          lotId,
          teamId,
          sharedByUserId: currentUserId,
        },
      }),
    );
    trackMlsMutation(
      ANALYTICS_EVENTS.MLS_LISTING_SHARED_WITH_TEAM,
      ANALYTICS_EVENTS.MLS_LISTING_SHARE_WITH_TEAM_FAILED,
      { lot_id: lotId, team_id: teamId },
      outcome,
    );
    refetch();
  };

  const requestRemoveShare = (
    shareId: string,
    teamName?: string,
    teamId?: string,
  ) => {
    setPendingRemoveShare({ shareId, teamName, teamId });
  };

  const confirmRemoveShare = async () => {
    if (pendingRemoveShare == null) {
      return;
    }
    const { shareId, teamId } = pendingRemoveShare;
    setPendingRemoveShare(null);
    const outcome = await runMlsMutation(() =>
      removeShare({
        variables: { shareId },
      }),
    );
    trackMlsMutation(
      ANALYTICS_EVENTS.MLS_SHARE_REMOVED,
      ANALYTICS_EVENTS.MLS_SHARE_REMOVE_FAILED,
      { lot_id: lotId, team_id: teamId },
      outcome,
    );
    refetch();
  };

  const handleApproveRequest = async (request: (typeof shareRequests)[0]) => {
    // Single mutation: update status from 'pending' to 'shared'
    const outcome = await runMlsMutation(() =>
      handleRequest({
        variables: {
          requestId: request.id,
          status: 'shared',
          handledByUserId: currentUserId,
        },
      }),
    );
    // Lifecycle (→ PostHog + Customer.io on success only): access granted.
    trackMlsMutation(
      ANALYTICS_EVENTS.MLS_ACCESS_REQUEST_HANDLED,
      ANALYTICS_EVENTS.MLS_ACCESS_REQUEST_HANDLE_FAILED,
      {
        lot_id: lotId,
        team_id: request.team_id,
        decision: 'approved',
        source_surface: 'listing_settings',
      },
      outcome,
      { lifecycle: true },
    );
    refetch();
  };

  const handleRejectRequest = async (requestId: string) => {
    const outcome = await runMlsMutation(() =>
      handleRequest({
        variables: {
          requestId,
          status: 'rejected',
          handledByUserId: currentUserId,
        },
      }),
    );
    trackMlsMutation(
      ANALYTICS_EVENTS.MLS_ACCESS_REQUEST_HANDLED,
      ANALYTICS_EVENTS.MLS_ACCESS_REQUEST_HANDLE_FAILED,
      {
        lot_id: lotId,
        decision: 'rejected',
        source_surface: 'listing_settings',
      },
      outcome,
      { lifecycle: true },
    );
    refetch();
  };

  const isLoading =
    updating || sharing || removing || handlingRequest || loading;

  return (
    <Box sx={{ pointerEvents: isLoading ? 'none' : 'auto' }}>
      {/* Settings Section */}
      <MarketingHomogeneousTitle title={t('Settings')} />
      {!canUpdate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'Only the listing broker or their team leader can update MLS settings',
          )}
        </Alert>
      )}
      <FormControlLabel
        {...tourAnchor(MLS_TOUR_ANCHORS.addListing)}
        disabled={!canUpdate}
        control={
          <Switch
            checked={lot.shared_on_mls_at != null}
            onChange={handleToggleMls}
          />
        }
        label={t('Add listing to MLS')}
      />
      {lot.shared_on_mls_at != null && (
        <Box sx={{ mt: 2, ml: 2 }}>
          <RadioGroup
            value={lot.mls_share_mode ?? 'individual'}
            onChange={e => handleShareModeChange(e.target.value)}
            row
          >
            <FormControlLabel
              value="individual"
              control={<Radio size="small" />}
              label={t('Share individually')}
              disabled={!canUpdate}
            />
            <FormControlLabel
              value="everyone"
              control={<Radio size="small" />}
              label={t('Share with everyone')}
              disabled={!canUpdate}
            />
          </RadioGroup>
        </Box>
      )}
      {lot.shared_on_mls_at != null && (
        <>
          {/* Terms Section */}
          <Box {...tourAnchor(MLS_TOUR_ANCHORS.terms)} sx={{ mt: 3 }}>
            <MarketingHomogeneousTitle title={t('Terms')} />

            <FormControlLabel
              disabled={!canUpdate}
              control={
                <Switch
                  checked={lot.mls_terms_enabled ?? false}
                  onChange={handleTermsToggle}
                />
              }
              label={t('Specify terms')}
            />

            {lot.mls_terms_enabled && (
              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 2, ml: 2, alignItems: 'flex-end' }}
              >
                <FormControl size="small" disabled={!canUpdate}>
                  <RaLabel label={t('Type')} />
                  <Select
                    size="small"
                    variant="outlined"
                    value={lot.mls_commission_type ?? 'percentage'}
                    onChange={e =>
                      handleCommissionTypeChange(
                        e.target.value as Mls_Commission_Type_Enum_Enum,
                      )
                    }
                    sx={{
                      background: 'var(--mui-palette-background-paper)',
                      minWidth: 150,
                      '& .MuiSelect-select > *': { minWidth: 0 },
                      '& .MuiSvgIcon-root': { background: 'transparent' },
                    }}
                  >
                    <MenuItem value="percentage">
                      {t('Commission split')}
                    </MenuItem>
                    <MenuItem value="fixed">{t('Fixed amount')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" disabled={!canUpdate}>
                  <RaLabel
                    label={t('Rate (buyer agent)')}
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                  {lot.mls_commission_type === 'percentage' ||
                  !lot.mls_commission_type ? (
                    <OutlinedInput
                      type="number"
                      value={commissionPercent}
                      onChange={e => setCommissionPercent(e.target.value)}
                      onBlur={handleCommissionPercentBlur}
                      endAdornment={
                        <InputAdornment position="end">%</InputAdornment>
                      }
                      inputProps={{ min: 0, max: 100, step: 0.5 }}
                      sx={{
                        background: 'var(--mui-palette-background-paper)',
                        width: 120,
                      }}
                    />
                  ) : (
                    <OutlinedInput
                      type="number"
                      value={commissionFixed}
                      onChange={e => setCommissionFixed(e.target.value)}
                      onBlur={handleCommissionFixedBlur}
                      endAdornment={
                        <InputAdornment position="end">
                          {lot.currency ?? 'CHF'}
                        </InputAdornment>
                      }
                      inputProps={{ min: 0, step: 100 }}
                      sx={{
                        background: 'var(--mui-palette-background-paper)',
                        width: 120,
                      }}
                    />
                  )}
                </FormControl>
              </Stack>
            )}
          </Box>

          {/* Note Section */}
          <Box sx={{ mt: 3 }}>
            <MarketingHomogeneousTitle title={t('Note')} />
            <FormControl fullWidth size="small" disabled={!canUpdate}>
              <RaLabel label={t('Note for MLS viewers')} />
              <OutlinedInput
                multiline
                minRows={3}
                maxRows={8}
                placeholder={t(
                  'Add a note visible to agents viewing this listing on MLS...',
                )}
                value={mlsNote}
                onChange={e => setMlsNote(e.target.value)}
                onBlur={async () => {
                  if (mlsNote !== (lot.mls_note ?? '')) {
                    const outcome = await runMlsMutation(() =>
                      updateMlsField({
                        variables: {
                          lotId,
                          changes: { mls_note: mlsNote || null },
                        },
                      }),
                    );
                    trackTermsUpdated('note', outcome, {
                      hasNote: mlsNote.trim() !== '',
                    });
                  }
                }}
                sx={{ background: 'var(--mui-palette-background-paper)' }}
              />
            </FormControl>
          </Box>

          {/* Network Section */}
          <Box sx={{ mt: 3 }}>
            <MarketingHomogeneousTitle title={t('Network')} />

            {networkLoading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Network Stats */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent sx={{ py: 2 }}>
                <Stack
                  direction="row"
                  spacing={0}
                  divider={
                    <Box sx={{ borderRight: 1, borderColor: 'divider' }} />
                  }
                >
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('Agencies / Teams')}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {networkTeamsTotal}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('Listing shared with')}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {listingShares.length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('Share requests')}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {pendingRequests.length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Toolbar Row with QuickFilters */}
            <Box sx={{ mb: 2, py: 1 }}>
              <Filters
                tableName="mls_network_teams"
                quickFilters={networkTeamsQuickFilters}
                expandedFilterType="quick"
                queryParamsScope="mls_network"
              />
            </Box>

            {/* Shared Agencies Section */}
            {listingShares.length > 0 && lot.mls_share_mode !== 'everyone' && (
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{ px: 2, py: 1, bgcolor: 'success.light' }}>
                  <Typography variant="subtitle2">
                    {t('Shared with')} ({listingShares.length})
                  </Typography>
                </Box>
                <List dense>
                  {listingShares.map((share: ListingShare) => {
                    const teamDetail = sharedTeamsById.get(share.team_id);
                    const name = teamDetail?.name ?? share.team?.name;
                    const locality = teamDetail?.team?.locality;
                    const postcode = teamDetail?.team?.postcode;
                    const logoUrl = teamDetail?.logo_url;
                    return (
                      <ListItem key={share.id}>
                        <Avatar
                          src={logoUrl ?? undefined}
                          variant="rounded"
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'grey.100',
                            mr: 1.5,
                            flexShrink: 0,
                          }}
                        >
                          {name?.[0]?.toUpperCase()}
                        </Avatar>
                        <ListItemText
                          primary={name}
                          secondary={
                            locality
                              ? `${postcode ?? ''} ${locality}`.trim()
                              : undefined
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title={t('Remove sharing')}>
                            <IconButton
                              edge="end"
                              size="small"
                              disabled={!canUpdate}
                              onClick={() =>
                                requestRemoveShare(
                                  share.id,
                                  name ?? undefined,
                                  share.team_id,
                                )
                              }
                            >
                              <Trash2Icon size={18} />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )}

            {/* Pending Requests Section */}
            {pendingRequests.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{ px: 2, py: 1, bgcolor: 'warning.light' }}>
                  <Typography variant="subtitle2">
                    {t('Pending access requests')} ({pendingRequests.length})
                  </Typography>
                </Box>
                <List dense>
                  {pendingRequests.map((request: ShareRequest) => (
                    <ListItem key={request.id}>
                      <ListItemText
                        primary={request.team?.name}
                        secondary={`${t('Requested by')} ${
                          request.requester_name ?? '—'
                        } - ${new Date(request.created_at).toLocaleDateString(
                          locale,
                        )}`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title={t('Approve')}>
                          <IconButton
                            edge="end"
                            color="success"
                            disabled={!canUpdate}
                            onClick={() => handleApproveRequest(request)}
                            sx={{ mr: 1 }}
                          >
                            <CircleCheckIcon size={20} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Reject')}>
                          <IconButton
                            edge="end"
                            color="error"
                            disabled={!canUpdate}
                            onClick={() => handleRejectRequest(request.id)}
                          >
                            <Trash2Icon size={20} />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Agency Table View - with empty state handling */}
            {networkTeamsCount === 0 && !hasActiveFilters ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('No agencies found in your network')}
                </Typography>
              </Paper>
            ) : networkTeams.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('No agencies match your search')}
                </Typography>
              </Paper>
            ) : (
              <RaDataGrid
                rows={networkTeams.map((team: NetworkTeam) => ({
                  ...team,
                  id: team.id ?? '',
                  _isShared: sharedTeamIds.has(team.id ?? ''),
                  _share: listingShares.find(
                    (s: ListingShare) => s.team_id === team.id,
                  ),
                  _requestedCount: getTeamRequestedCount(team.id ?? ''),
                  _isPublished: isTeamPublished(team.id ?? ''),
                }))}
                columns={
                  [
                    {
                      field: 'name',
                      headerName: t('Agency'),
                      flex: 1,
                      minWidth: 250,
                      renderCell: params => {
                        const logoUrl = params.row.logo_url;
                        return (
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                              alignItems: 'center',
                              py: 1,
                            }}
                          >
                            <Avatar
                              src={logoUrl ?? undefined}
                              variant="rounded"
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: 'grey.100',
                              }}
                            >
                              {params.row.name?.[0]?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                }}
                              >
                                {params.row.name}
                              </Typography>
                              {params.row.team?.locality && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  {params.row.team.postcode}{' '}
                                  {params.row.team.locality}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        );
                      },
                    },
                    {
                      field: 'sharing',
                      headerName: t('Sharing'),
                      width: 100,
                      align: 'center',
                      headerAlign: 'center',
                      sortable: false,
                      renderCell: params => {
                        const isShareDisabled =
                          !canUpdate || lot.mls_share_mode === 'everyone';
                        return (
                          <Switch
                            size="small"
                            checked={params.row._isShared}
                            disabled={isShareDisabled}
                            onClick={e => e.stopPropagation()}
                            onChange={() =>
                              params.row._isShared && params.row._share
                                ? requestRemoveShare(
                                    params.row._share.id,
                                    params.row.name ?? undefined,
                                    params.row.id,
                                  )
                                : handleShareWithTeam(params.row.id)
                            }
                          />
                        );
                      },
                    },
                    {
                      field: 'requested',
                      headerName: t('Requested'),
                      width: 120,
                      align: 'center',
                      headerAlign: 'center',
                      sortable: false,
                      renderCell: params =>
                        params.row._requestedCount > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={params.row._requestedCount}
                          />
                        ) : (
                          '-'
                        ),
                    },
                    {
                      field: 'published',
                      headerName: t('Published on website'),
                      width: 180,
                      align: 'center',
                      headerAlign: 'center',
                      sortable: false,
                      renderCell: params =>
                        params.row._isPublished ? (
                          <CircleCheckIcon
                            size={18}
                            color={'var(--mui-palette-success-main)'}
                          />
                        ) : (
                          '-'
                        ),
                    },
                  ] as GridColDef[]
                }
                paginationMode="server"
                sortingMode="server"
                rowCount={networkTeamsCount}
                queryParamsScope="mls_network"
                pageSizeOptions={[10, 25, 50]}
                autoHeight
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                onRowClick={() => {}}
                sx={{
                  '& .MuiDataGrid-cell': {
                    py: 1,
                  },
                  '& .MuiDataGrid-row:hover': {
                    cursor: 'default',
                  },
                }}
              />
            )}
          </Box>
        </>
      )}
      {/* Remove access confirmation dialog */}
      <Dialog
        open={pendingRemoveShare != null}
        onClose={() => setPendingRemoveShare(null)}
        maxWidth="xs"
      >
        <DialogTitle>{t('Remove access?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingRemoveShare?.teamName != null
              ? t(
                  '{{teamName}} will no longer have access to this listing. They can request access again later.',
                  { teamName: pendingRemoveShare.teamName },
                )
              : t(
                  'This agency will no longer have access to this listing. They can request access again later.',
                )}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
            {t(
              'Any buyer leads they created against this listing will be removed.',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoveShare(null)}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={confirmRemoveShare}
            variant="contained"
            color="error"
            startIcon={<Trash2Icon />}
          >
            {t('Remove access')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Share mode change confirmation dialog */}
      <Dialog
        open={shareModeDialogOpen}
        onClose={handleShareModeDialogCancel}
        maxWidth="sm"
      >
        <DialogTitle>{t('Change sharing mode?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              '{{count}} agencies currently have individual access to this listing. Switching to "Share with everyone" will make this listing accessible to all agencies in your MLS network. Existing individual shares will no longer apply as all network members will have access.',
              { count: listingShares.length },
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShareModeDialogCancel}>{t('Cancel')}</Button>
          <Button onClick={handleShareModeDialogConfirm} variant="contained">
            {t('Continue')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Error snackbar */}
      <Snackbar
        open={errorMessage != null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert
          severity="error"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
